import {
  ASH_ADMIN,
  ORGANIZATION_ADMIN,
  PROGRAM_ADMIN,
  NON_PHI_ORGANIZATION_ADMIN,
  NON_PHI_PROGRAM_ADMIN,
  type ClientPortalRole,
} from '@/constants/clientPortalRoles'
import {
  getRolesFromScopes,
  getRolesFromScope,
  findIdentityScopeByNormalizedPartnerId,
  rolesInclude,
  type IdentityScopes,
} from '@/utilities/auth/identityScopeRoles'

export interface RoleSyncStrategy {
  isAshAdmin(scopes: IdentityScopes | undefined): boolean
  isOrgAdmin(scopes: IdentityScopes | undefined): boolean
  isOrgAdminForPartner(scopes: IdentityScopes | undefined, partnerId?: string | null): boolean
  isProgramAdminForPartner(scopes: IdentityScopes | undefined, partnerId?: string | null): boolean
  isAnyPortalAdmin(scopes: IdentityScopes | undefined): boolean
  getPortalAdminRoles(scopes: IdentityScopes | undefined): string[]
  resolveTenantSlug(
    partnerId?: string | null,
    orgSlug?: string | null,
    isAshAdmin?: boolean,
  ): string | null
}

export class DefaultRoleSyncStrategy implements RoleSyncStrategy {
  public isProgramAdminForPartner(
    scopes: IdentityScopes | undefined,
    partnerId?: string | null,
  ): boolean {
    const scope = findIdentityScopeByNormalizedPartnerId(scopes, partnerId)
    return rolesInclude(getRolesFromScope(scope ?? {}), [PROGRAM_ADMIN, NON_PHI_PROGRAM_ADMIN])
  }

  public isOrgAdminForPartner(
    scopes: IdentityScopes | undefined,
    partnerId?: string | null,
  ): boolean {
    const scope = findIdentityScopeByNormalizedPartnerId(scopes, partnerId)
    return rolesInclude(getRolesFromScope(scope ?? {}), [
      ORGANIZATION_ADMIN,
      NON_PHI_ORGANIZATION_ADMIN,
    ])
  }

  public isAshAdmin(scopes: IdentityScopes | undefined): boolean {
    return rolesInclude(getRolesFromScopes(scopes), [ASH_ADMIN])
  }

  public isOrgAdmin(scopes: IdentityScopes | undefined): boolean {
    return rolesInclude(getRolesFromScopes(scopes), [
      ORGANIZATION_ADMIN,
      NON_PHI_ORGANIZATION_ADMIN,
    ])
  }

  public isAnyPortalAdmin(scopes: IdentityScopes | undefined): boolean {
    const roles = new Set(getRolesFromScopes(scopes).map((r) => r.toLowerCase()))
    if (
      roles.has(ASH_ADMIN.toLowerCase()) ||
      roles.has(ORGANIZATION_ADMIN.toLowerCase()) ||
      roles.has(NON_PHI_ORGANIZATION_ADMIN.toLowerCase()) ||
      roles.has(PROGRAM_ADMIN.toLowerCase()) ||
      roles.has(NON_PHI_PROGRAM_ADMIN.toLowerCase())
    ) {
      return true
    }

    // Fallback: handle potential naming variations e.g. different separators or capitalization.
    const hasPatternMatch = Array.from(roles).some(
      (r) => r.startsWith('client-portal.') && (r.includes('admin') || r.includes('administrator')),
    )
    return hasPatternMatch
  }

  public getPortalAdminRoles(scopes: IdentityScopes | undefined): string[] {
    const allRoles = Array.from(new Set(getRolesFromScopes(scopes)))
    const constants = new Set([
      ASH_ADMIN,
      ORGANIZATION_ADMIN,
      PROGRAM_ADMIN,
      NON_PHI_ORGANIZATION_ADMIN,
      NON_PHI_PROGRAM_ADMIN,
    ])

    const exact = allRoles.filter((r) => constants.has(r as ClientPortalRole))
    if (exact.length > 0) return exact

    // Case-insensitive check against known constants (without requiring any prefix pattern)
    const roles = allRoles.map((r) => r.toLowerCase())
    const ash = ASH_ADMIN.toLowerCase()
    const org = ORGANIZATION_ADMIN.toLowerCase()
    const nonPhiOrg = NON_PHI_ORGANIZATION_ADMIN.toLowerCase()
    const program = PROGRAM_ADMIN.toLowerCase()
    const nonPhiProgram = NON_PHI_PROGRAM_ADMIN.toLowerCase()

    if (roles.includes(ash)) return [ASH_ADMIN]
    if (roles.includes(nonPhiOrg)) return [NON_PHI_ORGANIZATION_ADMIN]
    if (roles.includes(org)) return [ORGANIZATION_ADMIN]
    if (roles.includes(nonPhiProgram)) return [NON_PHI_PROGRAM_ADMIN]
    if (roles.includes(program)) return [PROGRAM_ADMIN]

    // Pattern fallback: shapes differ but clearly indicate portal admin access
    const hasPortalAdmin = roles.some(
      (r) => r.startsWith('client-portal.') && (r.includes('admin') || r.includes('administrator')),
    )
    return hasPortalAdmin ? [ORGANIZATION_ADMIN] : []
  }

  public resolveTenantSlug(
    partnerId?: string | null,
    orgSlug?: string | null,
    isAshAdmin?: boolean,
  ): string | null {
    const slug = (partnerId || orgSlug || '').toLowerCase().trim()
    if (!slug) return null
    if (slug === 'client' && !isAshAdmin) return null
    return slug
  }
}
